import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import StyledDrawer from "../StyledDrawer";
import SmallSearchBox from "../../common/SmallSearchBox";
import DialogTopPanel from "../../common/DialogTopPanel";
import CommonTable from "../../common/CommonTable";
import { useSnackbar } from "notistack";
import API from "../../../utils/axios";
import { capitalize } from "../../../utils/helpers";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import TitleWithEducationalMaterials from "../../common/TitleWithEducationalMaterials";

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: "1px solid #DDD",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 1
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  },
  iconButton: {
    marginLeft: "-0.5rem",
    marginRight: theme.spacing(2),
    "& svg": {
      width: theme.spacing(3),
      height: theme.spacing(3)
    }
  },
  topPanelWrapper: {
    padding: theme.spacing(1.25, 0, 1.25, 3)
  },
  iconPositionFix: {
    margin: theme.spacing(-0.25, 0)
  }
}));

function TagsOverviewDialog({
  open,
  setCurrentAction,
  setCurrentTag,
  tags,
  tagsLoaded,
  setTags,
  handleClose,
  backFromEditAction
}) {
  const classes = useStyles();
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const tagsCount = `${tags.length} tag${tags.length > 1 ? "s" : ""}`;
  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false
  });

  const [filteredTags, setFilteredTags] = useState([]);

  const { enqueueSnackbar } = useSnackbar();

  const handleDelete = tag => {
    const { id, name } = tag;

    setLoading(true);
    API.delete(`/v1/tags/${id}`)
      .then(res => {
        setTags(tags.filter(tag => tag.id !== id));
        setSelected(selected.filter(tagId => tagId !== id));
        enqueueSnackbar(`Tag "${name}" deleted.`);
      })
      .catch(e => {
        enqueueSnackbar("Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFilter = (tag, value) => {
    return (
      (tag.name || "").toLowerCase().indexOf(value.toLowerCase()) !== -1 ||
      (tag.type || "").toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  };

  useEffect(() => {
    setFilteredTags(tags.filter(tag => handleFilter(tag, searchInputValue)));
  }, [tags, searchInputValue]);

  const handleMultipleDelete = () => {
    setLoading(true);

    let tag;
    if (selected.length === 1) {
      tag = tags.find(item => item.id === selected[0]);
    }

    API.delete(`/v1/tags/batch`, { data: selected })
      .then(res => {
        setTags(tags.filter(tag => !selected.includes(tag.id)));
        setSelected([]);
        enqueueSnackbar(
          !tag ? `Selected tags deleted.` : `Tag "${tag.name}" deleted.`
        );
      })
      .catch(e => {
        enqueueSnackbar("Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearchChange = val => {
    setSearchInputValue(val);
  };

  return (
    <StyledDrawer
      handleClose={handleClose}
      open={open}
      loading={loading}
      noAnim={backFromEditAction}
      largerWidth
    >
      <Box
        className={classes.header}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={3}
      >
        <Box
          height={80}
          alignItems="center"
          justifyContent="space-between"
          display="flex"
        >
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleClose} className={classes.iconButton}>
              <CloseIcon />
            </IconButton>
            <TitleWithEducationalMaterials
              title={"Tags"}
              configId={"tags"}
              sectionName={"tags"}
              showArticle
              showDivider
            />
          </Box>
        </Box>
      </Box>
      <div className={classes.topPanelWrapper}>
        <Grid container wrap="nowrap">
          <Grid item xs>
            <DialogTopPanel
              content="Add tag"
              dataAddCy="add-new-tag"
              dataDeleteCy={"deleteAllTags"}
              numSelected={selected.length}
              count={tags.length}
              itemsCount={tagsCount}
              tooltipDelete={"Delete selected tags"}
              iconClassName={classes.iconPositionFix}
              handleAdd={() => {
                setCurrentAction("edit");
                setCurrentTag(null);
              }}
              handleMultipleDelete={() => {
                setConfirmDialogState({
                  open: true,
                  title: `Delete tags?`,
                  body: `Are you sure you want to permanently delete ${
                    selected.length
                  } tag${selected.length > 1 ? "s" : ""}?`,
                  action: handleMultipleDelete
                });
              }}
            />
          </Grid>
          <Grid item style={{ marginRight: 24 }}>
            <SmallSearchBox
              searchText={searchInputValue}
              onSearchChanged={handleSearchChange}
              ariaLabel="Search tags"
              dataCy="tags-search"
            />
          </Grid>
        </Grid>
      </div>
      <CommonTable
        rows={filteredTags.map(tag => ({
          ...tag,
          type: tag.type && capitalize(tag.type)
        }))}
        setCurrentItem={setCurrentTag}
        setCurrentAction={setCurrentAction}
        handleDelete={handleDelete}
        selected={selected}
        setSelected={setSelected}
        loading={!tagsLoaded}
        label={"tag"}
        setConfirmDialogState={setConfirmDialogState}
        searchInputValue={searchInputValue}
      />
      <ConfirmDialog
        isProject
        open={confirmDialogState.open}
        handleClose={() =>
          setConfirmDialogState({ ...confirmDialogState, open: false })
        }
        action={confirmDialogState.action}
        title={confirmDialogState.title}
        body={confirmDialogState.body}
      />
      {/* <SelectableList data={tags} setCurrentItem={setCurrentTag} /> */}
    </StyledDrawer>
  );
}

export default TagsOverviewDialog;
